import {
  configureStore,
  getDefaultMiddleware,
  combineReducers,
  Action,
  CombinedState,
} from '@reduxjs/toolkit';
import {
  createStateSyncMiddleware,
  withReduxStateSync,
  initStateWithPrevTab,
} from 'redux-state-sync';
import { createBrowserHistory } from 'history';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { persistStore, persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storageSession from 'redux-persist/lib/storage/session';
import thunkMiddleware from 'redux-thunk';
import { encryptTransform } from 'redux-persist-transform-encrypt';
import reducers from 'modules';
import { actionsToIgnore, stateSyncWhiteList } from './helpers';

const history = createBrowserHistory();
const routeMiddleware = routerMiddleware(history);

const createRootReducer = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: CombinedState<any>,
  action: Action<string>,
) => {
  if (action.type === 'USER_LOGOUT') {
    Object.keys(state).forEach((key) =>
      storageSession.removeItem(`persist:${key}`),
    );
    state = {};
  }
  return combineReducers({
    ...reducers,
    router: connectRouter(history),
  })(state, action);
};
withReduxStateSync(createRootReducer);

export type RootState = ReturnType<typeof createRootReducer>;

const encryptor = encryptTransform({
  secretKey: process.env.REACT_APP_ENCRYPTOR_SECRET_KEY || '',
  onError: (error) => {
    console.error(error);
  },
});

const persistConfig = {
  key: 'root',
  storage: storageSession,
  stateReconciler: autoMergeLevel2,
  blacklist: [],
  transforms: [encryptor],
};

const pReducer = persistReducer<RootState>(persistConfig, createRootReducer);

const middlewares = [
  routeMiddleware,
  thunkMiddleware,
  createStateSyncMiddleware({
    channel: 'car_oi_channel',
    broadcastChannelOption: { type: 'localstorage' },
    whitelist: stateSyncWhiteList,
  }),
];

const store = configureStore({
  reducer: pReducer,
  middleware: getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: actionsToIgnore,
      ignoredPaths: [
        'alertHandler.primaryButtonAction',
        'alertHandler.secondaryButtonFunction',
      ],
    },
  }).prepend(...middlewares),
  devTools:
    process.env.REACT_APP_ENABLE_REDUX_DEV_TOOLS === 'true' ||
    process.env.NODE_ENV === 'development',
});
initStateWithPrevTab(store);

const persistor = persistStore(store);

if (
  module.hot &&
  process.env.NODE_ENV !== 'production' &&
  process.env.NODE_ENV !== 'test'
) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const newRootReducer = require('../modules').reducers;
  module.hot.accept('../modules', () => store.replaceReducer(newRootReducer));
}

export type AppDispatch = typeof store.dispatch;

export { store, history, persistor };
